// A deliberately tiny promise wrapper over IndexedDB for the desk store — two
// object stores, get/put/delete, and whole-database deletion. Not a general
// IDB library (idb-keyval & co. would work, but this is ~60 lines and keeps
// the dependency surface of the eager bundle flat).
//
//   meta  — the CryptoKey handle, the owning user id, the store version.
//           Nothing sensitive is ever written here in clear.
//   vault — every other value, AES-GCM sealed (see crypto.ts / vault.ts).

export const DESK_DB_NAME = 'namibiago-desk';
const DB_VERSION = 1;
export type DeskStoreName = 'meta' | 'vault';

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function openDeskDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DESK_DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
      if (!db.objectStoreNames.contains('vault')) db.createObjectStore('vault');
    };
    request.onsuccess = () => {
      const db = request.result;
      // Another tab deleting the database (wipeLocalState) must not be
      // blocked by this connection staying open.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('desk database upgrade blocked by another tab'));
  });
}

export async function idbGet<T>(db: IDBDatabase, store: DeskStoreName, key: string): Promise<T | undefined> {
  const tx = db.transaction(store, 'readonly');
  return (await promisify(tx.objectStore(store).get(key))) as T | undefined;
}

export async function idbPut(db: IDBDatabase, store: DeskStoreName, key: string, value: unknown): Promise<void> {
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).put(value, key);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function idbDelete(db: IDBDatabase, store: DeskStoreName, key: string): Promise<void> {
  const tx = db.transaction(store, 'readwrite');
  tx.objectStore(store).delete(key);
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Deletes the whole desk database. Resolves once the delete has completed;
 * other tabs' connections close themselves via `onversionchange`. */
export function deleteDeskDb(): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DESK_DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    // `blocked` still completes once the other connections close.
    request.onblocked = () => undefined;
  });
}
